import axios from 'axios';
import * as types from '@/store/mutation-types';
import {Form} from '@/plugins/FWForms/index';
import {performApiRequest} from '@/store/helpers/vuex-helpers';
import {handleApiError} from '@/utils/errorHandler.ts';
import type {ActionContext, Module} from 'vuex';
import type {RootState} from '@/types/RootState';
import type {ListingInterface} from '@/types/listing';
import type {StoreLoaders} from '@/types/common';

export type CheckoutLoadingState = StoreLoaders & {
    checkout: boolean;
    checkAvailability: boolean;
    paymentIntent: boolean;
    checkDrivingDistance: boolean;
};

export type AuthenticatedRenter = {
    email: string;
    first_name: string;
    last_name: string;
    phone: string;
    dob: string | null;
    custom_fields?: Record<string, unknown>;
};

export type RenterAuthenticationStartPayload = {
    email: string;
    slug?: string;
    domain?: string;
};

export type RenterAuthenticationStartResponse = {
    email: string;
    phone_last_digits: string | null;
    phone_number: string | null;
    verification_type: string;
};

export type RenterAuthenticationCompletePayload = {
    code: string;
    email: string;
    domain: string;
    slug?: string;
    verification_type?: string;
};

export type RenterAuthenticationCompleteResponse = {
    renter: AuthenticatedRenter;
};

export type ListingAvailabilitySummary = {
    available: boolean;
    part: string | null;
    booking: string | null;
    nextAvailableStartTime: string | null;
    nextAvailableEndTime: string | null;
    nextAvailableStartDate: string | null;
    exception?: string;
};

export type CatalogListingAvailability = Pick<
    ListingInterface,
    'l_id' | 'name' | 'description' | 'active' | 'paused' | 'approved' | 'minimum_request_duration' | 'checkout_mode' | 'checkout_url'
> & {
    available: ListingAvailabilitySummary;
    pricing: {
        amount: number;
        adjusted_amount: number;
        price_period: string;
    };
    listing_image: string | null;
    vehicle: {
        make: string | null;
        model: string | null;
        year: number | null;
        seats: number | null;
        doors: number | null;
        details: Array<{ type: string; label: string }>;
    };
};

export type ListingsAvailabilityResponse = {
    listings: CatalogListingAvailability[];
    fra1: number;
};

export interface ListingsAvailabilityPayload {
    dates: {
        start: string;
        end: string;
    };
    l_id?: string;
    ignoreUnavailable?: boolean;
}

export interface CheckoutState {
    loading: CheckoutLoadingState;
    checkoutForm: Form<Record<string, unknown>>;
    initialized: boolean;
    checkoutRenterAuthenticatedData: AuthenticatedRenter | null;
}

const state: CheckoutState = {
    loading: {
        checkout: false,
        checkAvailability: false,
        paymentIntent: false,
        checkDrivingDistance: false,
    },
    checkoutForm: new Form(),
    checkoutRenterAuthenticatedData: null,
    initialized: false,
};

const getters = {
    loading: (state: CheckoutState) => state.loading,
    checkoutForm: (state: CheckoutState) => state.checkoutForm,
    checkoutRenterAuthenticatedData: (state: CheckoutState) => state.checkoutRenterAuthenticatedData,
    checkoutInitialized: (state: CheckoutState) => state.initialized,
};

const mutations = {
    [types.SET_CHECKOUT_LOADING](state: CheckoutState, loading: { subject: string; loading: boolean }) {
        state.loading[loading.subject] = loading.loading;
    },
    [types.CLEAR_CHECKOUT_FORM_ERRORS](state: CheckoutState, form_field?: string | null) {
        if (form_field === null || form_field === undefined) {
            state.checkoutForm.errors.clear();
        } else {
            state.checkoutForm.errors.clear(form_field);
        }
    },
    [types.SET_CHECKOUT_INITIALIZED](state: CheckoutState, initialized: boolean = true) {
        state.initialized = initialized;
    },
    [types.SET_CHECKOUT_RENTER_AUTHENTICATED](state: CheckoutState, data: AuthenticatedRenter) {
        state.checkoutRenterAuthenticatedData = data;
    },
};

const actions = {
    async renterAuthenticationStart(
        {commit}: ActionContext<CheckoutState, RootState>,
        data: RenterAuthenticationStartPayload,
    ) {
        try {
            const responseData = await performApiRequest<RenterAuthenticationStartResponse>(
                commit,
                'SET_CHECKOUT_LOADING',
                'checkAvailability',
                () => axios.post('/v1/checkout/renter-authentication/start', data),
            );

            return {
                email: responseData.email,
                phone_number: responseData.phone_number,
            };
        } catch (error) {
            handleApiError(error);
        }
    },

    async renterAuthenticationComplete(
        {commit}: ActionContext<CheckoutState, RootState>,
        data: RenterAuthenticationCompletePayload,
    ) {
        try {
            const responseData = await performApiRequest<RenterAuthenticationCompleteResponse>(
                commit,
                'SET_CHECKOUT_LOADING',
                'checkAvailability',
                () => axios.post('/v1/checkout/renter-authentication/complete', data),
            );
            commit(types.SET_CHECKOUT_RENTER_AUTHENTICATED, responseData.renter);

            return true;
        } catch (error) {
            handleApiError(error);

            return false;
        }
    },

    async checkListingsAvailability(
        {commit, rootGetters}: ActionContext<CheckoutState, RootState>,
        data: ListingsAvailabilityPayload,
    ) {
        const params = new URLSearchParams({
            start: data.dates.start,
            end: data.dates.end,
        });

        if (data.l_id) {
            params.append('l_id', data.l_id);
        }

        if (data.ignoreUnavailable) {
            params.append('ignoreUnavailable', 'true');
        }

        try {
            const companyUuid = rootGetters['company/company']?.uuid as string | undefined;

            if (!companyUuid) {
                return;
            }

            const url = `/v1/company/${companyUuid}/listings/availability?${params.toString()}`;

            const responseData = await performApiRequest<ListingsAvailabilityResponse>(
                commit,
                'SET_CHECKOUT_LOADING',
                'checkAvailability',
                () => axios.get(url),
            );

            commit('listing/SET_ALL_LISTINGS', responseData?.listings ?? [], {root: true});
        } catch (error) {
            console.error('Error fetching listings availability:', error);
        }
    },
};

const checkoutModule: Module<CheckoutState, RootState> = {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};

export default checkoutModule;
